import Vue from "vue";
import { mapGetters } from "vuex";

import { LoadingSpinner, StaticFormControl } from "@/components";
import { formatDateTime, stringHelper } from "@/components/mixins";
import { CurrencyService, PartDetailServices } from "@/services";
import { Nullable, PartValidationReason } from "@/types";
import ToolTipPopOver from "@/components/Elements/ToolTipPopover.vue"
import { FeatureFlagService } from "@/services/FeatureFlagService"
import { constants }           from "@/const"

// Components
import { default as AddToCart } from "@/components/PartDetail/AddToCart/AddToCart.vue";
import { default as PartStatusMessage } from "@/components/PartDetail/PartStatusMessage/PartStatusMessage.vue";

export default Vue.extend({
    name: "DetailsAndPricing",

    components: {
        AddToCart,
        LoadingSpinner,
        StaticFormControl,
        PartStatusMessage,
        ToolTipPopOver
    },
    mixins: [formatDateTime, stringHelper],
    props: {
        details: {
            type: Object,
            required: true,
        },
        addToPartList: Function
    },

    data: () => ({
        readyToBuildQuantity: null,
        showReadyToBuildToolTip: false,
        readyToBuildToolTipText: `This is a part that we assemble into a kit.
                                    <br />We currently have enough materials to build the quantity displayed.
                                    <br />
                                    <br />
                                    <em>Note:  Orders placed later in the day may ship<br />the following day due to build time.</em>`,
        isUnallocatedProductsEnabled: false,
        isEstimatedAvailabilityEnabled: false,
    }),
    computed: {
        ...mapGetters([
            "getContentItem",
            "billingCurrencyCode",
            "tenantDetails",
            "isPriceVisible",
            "IsMcfa",
            "IsMcfe",
            "IsMcfs",
            "activeAccountId",
            "canSeeContractPrice",
            "canSeeDealerNet",
            "hasPermission",
            "cartSummary",
            "canSeeAnyPrice",
        ]),
        combinedUnallocatedProducts: function () {

            const product = this.details.UnallocatedProducts.filter(function (product) {                
                return product;
            })
           
           // add Inbound Past Due to EstimatedArrivalDate
           for (let index in product) {
            var newDate = this.getIncomingStockDate(product[index]);
            product[index].EstimatedArrivalDate = newDate;
           } 

          let inboundPastDueProduct = product.filter(item => 
            item.EstimatedArrivalDate.toString().includes('Due')
          );

          let nonInboundPastDueProduct = product.filter(item => 
            !item.EstimatedArrivalDate.toString().includes('Due')
          );
          
          let combinedArrayByWarehouseCode = [];
          for(let newIndex in inboundPastDueProduct) {

            if(typeof combinedArrayByWarehouseCode[inboundPastDueProduct[newIndex].WarehouseCode] != "undefined" ) {
                combinedArrayByWarehouseCode[inboundPastDueProduct[newIndex].WarehouseCode].UnallocatedQuantity = combinedArrayByWarehouseCode[inboundPastDueProduct[newIndex].WarehouseCode].UnallocatedQuantity + inboundPastDueProduct[newIndex].UnallocatedQuantity;
            } else {
                combinedArrayByWarehouseCode[inboundPastDueProduct[newIndex].WarehouseCode] = inboundPastDueProduct[newIndex];
            }
          }

                // this takes our sorted/filtered arrays which were split by warehouse/inbound past due and warehouse/not yet due
                  var newArray = [] as Array<any>;

                  for(let indexNumber in combinedArrayByWarehouseCode) {
                    const container = {
                        WarehouseCode: combinedArrayByWarehouseCode[indexNumber].WarehouseCode, 
                        UnallocatedQuantity:combinedArrayByWarehouseCode[indexNumber].UnallocatedQuantity, 
                        EstimatedArrivalDate: combinedArrayByWarehouseCode[indexNumber].EstimatedArrivalDate
                    };
                    newArray.push(container);
                  }
                  
                  return newArray.concat(nonInboundPastDueProduct);
        },
        showMainWarehouseStockTemplate(){
            let hasWarehouseStock = false;
            if( this.IsMcfe ){
                hasWarehouseStock = this.details.WarehouseStockQuantities.some((x: any) => x.WarehouseCode === 'BE')
            }else if( this.IsMcfs){
                hasWarehouseStock = this.details.WarehouseStockQuantities.some((x: any) => x.WarehouseCode === 'SI')
            }else{
                return false;
            }

            return hasWarehouseStock;
        },
        showDekaCoreDoc(){
            return ['L01', 'L10'].includes(this.details.PicCode);
        },
        showAddToCart() {
            const validReasons = [
                PartValidationReason.IsValid,
                PartValidationReason.IsMao
            ]
            if (validReasons.includes(this.details.PartStatusMessage.PartValidationReason)) {
                return true;
            }
            return false;
        },
        showPrices() {

            if( !this.isPriceVisible )
            {
                return false
            }

            const codeMatches = ['A', 'B', 'C']
            // Dealer support would like pricing hidden on controller parts that are on soft freeze. 
            // The parts have a DealerNet price but by time they are programmed they actually are much more expensive. 
            if (this.IsMcfa &&
                this.details.PartStatusMessage.PartValidationReason === PartValidationReason.IsSoftFrozen &&
                this.details.PicCode === "608" &&
                !codeMatches.includes(this.details.OemPartCode.charAt(this.details.OemPartCode.length - 1))) {
                return false;
            }
            // We want to hide pricing if channel not authorized (mostly due but missed bulk pricing on part detail main page and the part alternatives display).
            // We also want to hide canceled, superseded and discontinued part's prices.
            const invalidReasons = [
                PartValidationReason.IsChannelNotAuthorized,
                PartValidationReason.IsCanceled,
                PartValidationReason.IsSuperseded,
                PartValidationReason.IsDiscontinued,
                PartValidationReason.ItemNotPriced,
                PartValidationReason.IsMaoNoPrice,
                PartValidationReason.IsProhibited,
                PartValidationReason.IsHardFrozen,
                PartValidationReason.IsMaoLimitExceededPriceNotCurrentNoStockQuantity,
                PartValidationReason.IsMaoLimitExceededPriceNotCurrent,
                PartValidationReason.MaoSoftFrozen
            ]
            if (invalidReasons.includes(this.details.PartStatusMessage.PartValidationReason)) {
                return false;
            }
            return true;
        },
        warehouseStockQuantities(){
            if( this.IsMcfe){
                return this.details.WarehouseStockQuantities.filter((x: any) => x.WarehouseCode === 'BE')
            } else if(this.IsMcfs){
                return this.details.WarehouseStockQuantities.filter((x: any) => x.WarehouseCode === 'SI')
            } else{
                return this.details.WarehouseStockQuantities;
            }          
        },
        otherWarehouses(){
            let otherWarehouses = [];
            if( this.IsMcfe ){
                otherWarehouses = this.details.WarehouseStockQuantities.filter((x: any) => x.WarehouseCode !== 'BE')
            }else if( this.IsMcfs){
                otherWarehouses = this.details.WarehouseStockQuantities.filter((x: any) => x.WarehouseCode !== 'SI')
            }else{
                return [];
            }
            
            return otherWarehouses;
        },
    },
    created() {
        this.getBuildableKits();
        this.isUnallocatedProductsEnabled = FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.UNALLOCATED_PRODUCTS);
        this.isEstimatedAvailabilityEnabled = FeatureFlagService.getFeatureEnabled(constants.FEATURE_NAMES.WAREHOUSE_LEAD_TIMES);
        this.combinedUnallocatedProducts = [];
    },
    methods: {
        formatCurrency(amount: Nullable<number>): string | null {
            return amount
                ? CurrencyService.formatCurrency(amount, this.details.CurrencyCode || this.billingCurrencyCode)
                : null;
        },
        showDiscountPrice() {
            return this.details.DiscountPrice && (this.details.DealerNetPrice == null || this.details.DiscountPrice < this.details.DealerNetPrice)
        },
        async getBuildableKits() {
            if (["RP","MA"].includes(this.details.ProvisionCode)) {
                var urlParams = {
                    accountId: this.activeAccountId,
                    userId: this.user.UserId,
                    partId: this.details.Id,
                    oemPartCode: this.details.OemPartCode
                }

                const buildableKits = await PartDetailServices.getBuildableKits(urlParams)            
                if (buildableKits.Items && buildableKits.Items[0]) {
                    this.readyToBuildQuantity = buildableKits.Items[0].QuantityCanBuild;
                }
            }
        },
        setCartQuantity(quantity: number) {
            this.$refs.addToCart.setQuantity(quantity);
        },
        getWarehouseDescription(warehouseCode){
            var warehouse = this.warehouseStockQuantities.find(warehouse => warehouse.WarehouseCode === warehouseCode);
            return warehouse.WarehouseDescription;
        },
        unallocatedProductsToShow(){
           return this.details.UnallocatedProducts.find(x => this.warehouseStockQuantities.find(y => y.WarehouseCode === x.WarehouseCode));
        },
        getIncomingStockDate(item){
            if( this.getTimeAgo(item.EstimatedArrivalDate).includes('ago')){
                return 'Inbound Past Due'
            }else{
                return this.formatDateTime(item.EstimatedArrivalDate);
            }               
        },
        hasUnallocatedProducts(details){
            return this.Any(details, "UnallocatedProducts") && this.unallocatedProductsToShow();
        },
        showUnallocatedProducts(details){
            let hasUnallocatedProducts = this.isUnallocatedProductsEnabled && this.hasUnallocatedProducts(details);
            return hasUnallocatedProducts;
        },
        hasEstimatedAvailability(details){
            this.EstimatedAvailabilities = this.Any(details, "EstimatedAvailabilities");
            return this.EstimatedAvailabilities
        },

        showEstimatedAvailability(details) {
            let hasEstimatedAvailability = this.isEstimatedAvailabilityEnabled && this.hasEstimatedAvailability(details);
            return hasEstimatedAvailability;
        },        
        showEstimatedShipDates(details){        
            // if the Unallocated Products feature flag is enabled, but there is no data there, defer to the Estimated Ship Date data.
            var hasEstimatedShipDates = this.Any(details, "EstimatedShipDates")
            if( this.isUnallocatedProductsEnabled){
                return !this.hasUnallocatedProducts(details) && hasEstimatedShipDates;
            }else{
                return hasEstimatedShipDates                 
            }          
        }
    },
});
